import { useRegisterSW } from 'virtual:pwa-register/react'
import { Button } from '@/ui/Button'
import { report } from '@/lib/telemetry'

/**
 * Offers a new build rather than installing it underneath the user.
 *
 * `skipWaiting` is off in the Workbox config on purpose: auto-activating a new
 * service worker mid-session can swap the running bundle while the camera is
 * live, which reads to the user exactly like the app "suddenly resetting".
 * The update lands when they choose it, or on the next cold launch.
 */
export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisterError(error: unknown) {
      report({
        level: 'warn',
        event: 'sw.register_failed',
        message: error instanceof Error ? error.message : String(error),
      })
    },
  })

  if (!needRefresh) return null

  return (
    <div
      role="status"
      className="pb-safe fixed inset-x-0 bottom-0 z-[60] mx-auto w-full max-w-md px-4 pb-4"
    >
      <div className="flex items-center gap-3 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-3 pl-4 shadow-xl">
        <p className="flex-1 text-[0.9375rem] font-medium">A new version is ready</p>
        <Button variant="ghost" onClick={() => setNeedRefresh(false)}>
          Later
        </Button>
        <Button variant="primary" onClick={() => void updateServiceWorker(true)}>
          Update
        </Button>
      </div>
    </div>
  )
}
